import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const PromptEditorModal = ({ prompt, content, onSave, onClose, saving }) => {
  const [editedContent, setEditedContent] = useState(content);
  const [selectedText, setSelectedText] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    setEditedContent(content);
  }, [content]);

  const handleSave = () => {
    onSave(editedContent);
  };

  // Markdown formatting helpers
  const insertText = (before, after = '', placeholder = '') => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = editedContent.substring(start, end);
    const textToInsert = selectedText || placeholder;
    
    const newText = 
      editedContent.substring(0, start) +
      before + textToInsert + after +
      editedContent.substring(end);
    
    setEditedContent(newText);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newPos = start + before.length + textToInsert.length;
      textarea.setSelectionRange(newPos, newPos + after.length);
    }, 0);
  };

  const formatBold = () => insertText('**', '**', 'bold text');
  const formatItalic = () => insertText('_', '_', 'italic text');
  const formatCode = () => insertText('`', '`', 'code');
  const formatCodeBlock = () => insertText('```\n', '\n```', 'code block');
  const formatHeading = (level) => {
    const hashes = '#'.repeat(level);
    insertText(`${hashes} `, '', 'Heading');
  };
  const formatList = () => insertText('- ', '', 'List item');
  const formatNumberedList = () => insertText('1. ', '', 'List item');
  const formatBlockquote = () => insertText('> ', '', 'Quote');
  const formatLink = () => insertText('[', '](url)', 'link text');
  const formatHorizontalRule = () => insertText('\n---\n', '');

  // Undo/Redo functionality
  const [history, setHistory] = useState([content]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const updateHistory = (newContent) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setEditedContent(newContent);
    updateHistory(newContent);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setEditedContent(history[historyIndex - 1]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setEditedContent(history[historyIndex + 1]);
    }
  };

  // Keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          formatBold();
          break;
        case 'i':
          e.preventDefault();
          formatItalic();
          break;
        case 'k':
          e.preventDefault();
          formatLink();
          break;
        case 'z':
          e.preventDefault();
          if (e.shiftKey) {
            redo();
          } else {
            undo();
          }
          break;
        case 'y':
          e.preventDefault();
          redo();
          break;
        default:
          break;
      }
    }
  };

  const wordCount = editedContent.split(/\s+/).filter(w => w.length > 0).length;
  const charCount = editedContent.length;
  const lineCount = editedContent.split('\n').length;

  // Custom components for ReactMarkdown
  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      return !inline && language ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language}
          PreTag="div"
          className="rounded-lg my-2"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="rounded-lg shadow-2xl w-full h-full max-w-7xl max-h-[95vh] flex flex-col" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{borderBottom: '1px solid var(--tqa-border-dark)'}}>
          <div>
            <h2 className="text-2xl font-bold" style={{color: 'rgba(255,255,255,0.85)'}}>
              {prompt.displayName}
            </h2>
            <p className="text-sm mt-1" style={{color: 'rgba(255,255,255,0.5)'}}>
              {prompt.description || 'Edit prompt content'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{color: 'rgba(255,255,255,0.4)'}}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.85)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-1 p-3" style={{borderBottom: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)'}}>
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 pr-3" style={{borderRight: '1px solid var(--tqa-border-dark)'}}>
            <button
              onClick={() => setShowPreview(false)}
              className={`toolbar-btn ${!showPreview ? 'bg-tqa-green-dim text-tqa-green border-tqa-green-dim' : ''}`}
              style={!showPreview ? {background:'rgba(130,255,207,0.15)',color:'var(--tqa-green)',borderColor:'rgba(130,255,207,0.35)'} : {}}
              title="Edit Mode"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => setShowPreview(true)}
              className={`toolbar-btn ${showPreview ? 'bg-tqa-green-dim text-tqa-green border-tqa-green-dim' : ''}`}
              style={showPreview ? {background:'rgba(130,255,207,0.15)',color:'var(--tqa-green)',borderColor:'rgba(130,255,207,0.35)'} : {}}
              title="Preview Mode"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>

          {/* Text Formatting - Only show in edit mode */}
          {!showPreview && (
            <>
              <div className="flex items-center gap-1 pr-3" style={{borderRight: '1px solid var(--tqa-border-dark)'}}>
                <button
                  onClick={formatBold}
                  className="toolbar-btn"
                  title="Bold (Ctrl+B)"
                >
                  <strong>B</strong>
                </button>
                <button
                  onClick={formatItalic}
                  className="toolbar-btn"
                  title="Italic (Ctrl+I)"
                >
                  <em>I</em>
                </button>
                <button
                  onClick={formatCode}
                  className="toolbar-btn font-mono"
                  title="Inline Code"
                >
                  &lt;/&gt;
                </button>
                <button
                  onClick={formatCodeBlock}
                  className="toolbar-btn"
                  title="Code Block"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </button>
              </div>

              {/* Headings */}
              <div className="flex items-center gap-1 pr-3" style={{borderRight: '1px solid var(--tqa-border-dark)'}}>
                <button onClick={() => formatHeading(1)} className="toolbar-btn" title="Heading 1">H1</button>
                <button onClick={() => formatHeading(2)} className="toolbar-btn" title="Heading 2">H2</button>
                <button onClick={() => formatHeading(3)} className="toolbar-btn" title="Heading 3">H3</button>
              </div>

              {/* Lists and Quotes */}
              <div className="flex items-center gap-1 pr-3" style={{borderRight: '1px solid var(--tqa-border-dark)'}}>
                <button
                  onClick={formatList}
                  className="toolbar-btn"
                  title="Bullet List"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={formatNumberedList}
                  className="toolbar-btn"
                  title="Numbered List"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
                  </svg>
                </button>
                <button
                  onClick={formatBlockquote}
                  className="toolbar-btn"
                  title="Blockquote"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                </button>
              </div>

              {/* Insert */}
              <div className="flex items-center gap-1 pr-3" style={{borderRight: '1px solid var(--tqa-border-dark)'}}>
                <button
                  onClick={formatLink}
                  className="toolbar-btn"
                  title="Insert Link (Ctrl+K)"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </button>
                <button
                  onClick={formatHorizontalRule}
                  className="toolbar-btn"
                  title="Horizontal Rule"
                >
                  ―
                </button>
              </div>

              {/* Undo/Redo */}
              <div className="flex items-center gap-1">
                <button
                  onClick={undo}
                  disabled={historyIndex === 0}
                  className="toolbar-btn disabled:opacity-30"
                  title="Undo (Ctrl+Z)"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                </button>
                <button
                  onClick={redo}
                  disabled={historyIndex === history.length - 1}
                  className="toolbar-btn disabled:opacity-30"
                  title="Redo (Ctrl+Shift+Z)"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-hidden p-4">
          {showPreview ? (
            <div className="w-full h-full overflow-y-auto px-4 py-3 rounded-lg" style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)'}}>
              <div className="chat-markdown prose prose-sm max-w-none">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {editedContent}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={editedContent}
              onChange={handleContentChange}
              onKeyDown={handleKeyDown}
              className="w-full h-full px-4 py-3 rounded-lg shadow-sm focus:outline-none font-mono text-sm resize-none"
              style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              placeholder="Edit your prompt here..."
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4" style={{borderTop: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)'}}>
          <div className="flex items-center gap-4 text-xs" style={{color: 'rgba(255,255,255,0.5)'}}>
            <span>Type: <strong>{prompt.type}</strong></span>
            <span>Version: <strong>{prompt.version}</strong></span>
            <span>•</span>
            <span>{lineCount} lines</span>
            <span>•</span>
            <span>{wordCount} words</span>
            <span>•</span>
            <span>{charCount} characters</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="btn-secondary px-4 py-2 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || editedContent === content}
              className="btn-primary px-6 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .toolbar-btn {
          @apply px-2.5 py-1.5 text-sm font-medium rounded transition-colors flex items-center justify-center min-w-[32px];
          color: rgba(255,255,255,0.7);
          background: var(--tqa-surface-3);
          border: 1px solid var(--tqa-border-dark);
        }
        .toolbar-btn:hover {
          background: rgba(255,255,255,0.1);
        }
        .toolbar-btn:disabled {
          @apply cursor-not-allowed opacity-30;
        }
      `}</style>
    </div>
  );
};

export default PromptEditorModal;
